import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { clampScore, scenarioLabel, verdictWords } from '../../lib/voice'
import type { DraftView, FeedbackView } from '../../types/voice'

// The workbench: a brief in, a draft out, with the verifier's verdict beside it. What the person
// edits it into is sent back as feedback; a large edit becomes a preference the next draft is
// written under, and the edit distance is shown for what it is.
export function TryIt({ scenarios, modelConfigured, initialScenario = 'all' }: { scenarios: string[]; modelConfigured: boolean; initialScenario?: string }) {
  const spoke = useSpoke()
  const [scenario, setScenario] = useState<string>(initialScenario)
  const [brief, setBrief] = useState('')
  const [recipient, setRecipient] = useState('')
  const [draft, setDraft] = useState<DraftView | null>(null)
  const [edited, setEdited] = useState('')
  const [feedback, setFeedback] = useState<FeedbackView | null>(null)
  useEffect(() => setScenario(initialScenario), [initialScenario])

  const make = useMutation({
    mutationFn: () => voiceApi.draft(spoke, scenario === 'all' ? null : scenario, brief, recipient || null),
    onSuccess: (d) => {
      setDraft(d)
      setEdited(d.draftText)
      setFeedback(null)
    },
  })
  const send = useMutation({
    mutationFn: () => voiceApi.feedback(spoke, draft!.id, edited),
    onSuccess: setFeedback,
  })

  return (
    <div className="card workbench">
      <div className="card-head">
        <div>
          <h2>Draft in your voice</h2>
          <p className="muted">A brief in, a draft out, checked against your own mail before you see it. Edit it into what you would send; the edit teaches the profile.</p>
        </div>
      </div>
      {!modelConfigured && (
        <div className="refusal">
          <strong>No model connected</strong>
          <p>Drafting needs a model. Your profile is built and measured; once a model is configured for this instance, drafts are written from it and checked against it.</p>
        </div>
      )}
      <div className="workbench-grid">
        <div className="workbench-brief">
          <div className="try-grid">
            <label>
              Scenario
              <select value={scenario} onChange={(e) => setScenario(e.target.value)}>
                {['all', ...scenarios].map((s) => (
                  <option key={s} value={s}>
                    {scenarioLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Recipient first name
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Sarah" />
            </label>
            <label className="span-2">
              What should the message do?
              <textarea rows={4} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Thank Sarah for lunch and say I'll send the two names by Friday" />
            </label>
          </div>
          <button className="button primary" onClick={() => make.mutate()} disabled={!brief.trim() || make.isPending || !modelConfigured}>
            {make.isPending ? 'Drafting…' : 'Draft in my voice'}
          </button>
          {make.isError && (
            <div className="refusal">
              <strong>{make.error instanceof RefusedError ? make.error.refusal.reason : 'Unavailable'}</strong>
              <p>{make.error.message}</p>
            </div>
          )}
        </div>
        <div className="workbench-draft">
          {!draft && <p className="muted workbench-empty">{modelConfigured ? 'The draft lands here with its verdict.' : 'No model is connected on this instance.'}</p>}
          {draft && (
            <div className="draft">
              <div className="verdict">
                <div className="score" style={{ ['--score' as string]: clampScore(draft.verdict.score) }}>
                  <strong>{draft.verdict.score.toFixed(2)}</strong>
                </div>
                <div>
                  <strong>{verdictWords(draft.verdict.score, draft.verdict.passes)}</strong>
                  <p className="muted">
                    Centre {draft.verdict.centroidSimilarity.toFixed(2)} · nearest of your messages {draft.verdict.nearestExemplarSimilarity.toFixed(2)} · your own spread{' '}
                    {draft.verdict.profileCohesion.toFixed(2)} · {draft.attempts} {draft.attempts === 1 ? 'attempt' : 'attempts'} · {draft.modelId}
                  </p>
                  {draft.verdict.failing.length > 0 && <p className="error">Missed: {draft.verdict.failing.join('; ')}</p>}
                </div>
              </div>
              <label>
                The draft, edit it into what you would actually send
                <textarea rows={9} value={edited} onChange={(e) => setEdited(e.target.value)} />
              </label>
              <div className="actions">
                <button className="button" onClick={() => send.mutate()} disabled={send.isPending || edited.trim() === ''}>
                  This is what I sent
                </button>
                {feedback && (
                  <span className="muted">
                    {feedback.editedWords === 0
                      ? 'Sent as drafted.'
                      : `${feedback.editedWords} words changed (${Math.round(feedback.editDistance * 100)}% of the draft)${feedback.editDistance >= 0.25 ? '; noted as a preference for this scenario' : ''}.`}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
